import { useEffect, useState } from "react";
import apiFetch from "../../../../shared/services/apiClient";
import SendReceiptEmailModal from "./SendReceiptEmailModal";

/**
 * Banner "recibo(s) aplicado(s) → enviar correo".
 *
 * Al registrar un recibo nuevo (AddReceipt) ya se ofrecía enviar el
 * comprobante, pero al APLICAR un recibo que estaba pendiente —el caso de todo
 * lo que entra por importación de Excel— no: la pantalla saltaba al siguiente
 * de la cola y había que volver a la lista, filtrar y enviarlo a mano.
 *
 * Recibe solo los ids (es lo que viaja en el estado de la navegación entre un
 * recibo y el siguiente) y trae cada recibo del API al abrir el modal, que
 * necesita número, filial y monto para mostrarlos.
 *
 * Props:
 *   ids        — ids de los recibos aplicados
 *   autoOpen   — abre el modal apenas se monta (al terminar la cola)
 *   onDismiss  — cierra el banner
 */
export default function AppliedReceiptsEmailBanner({ ids = [], autoOpen = false, onDismiss }) {
  const [recibos, setRecibos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const [enviados, setEnviados] = useState(false);
  const [error, setError] = useState("");
  const clave = ids.join(",");

  async function abrir() {
    setError("");
    if (recibos) { setAbierto(true); return; }
    setCargando(true);
    try {
      const lista = await Promise.all(
        ids.map((id) =>
          apiFetch(`/api/finance/recibo/${id}/`).then((r) => (r.ok ? r.json() : null)),
        ),
      );
      const validos = lista.filter(Boolean);
      if (validos.length === 0) throw new Error("No se pudieron cargar los recibos.");
      setRecibos(validos);
      setAbierto(true);
    } catch (e) {
      setError(e.message || "No se pudieron cargar los recibos.");
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    setRecibos(null);
    setEnviados(false);
    if (autoOpen && ids.length > 0) abrir();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);

  if (ids.length === 0) return null;
  const n = ids.length;

  return (
    <>
      <div
        style={{
          background: "rgba(74,222,128,0.08)",
          border: "1px solid rgba(74,222,128,0.3)",
          borderLeft: "4px solid #4ade80",
          borderRadius: 8,
          padding: "12px 18px",
          marginBottom: 14,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <span style={{ color: "#4ade80", fontWeight: 500, fontSize: "0.9rem" }}>
          <i className="fa-solid fa-circle-check" style={{ marginRight: 8 }} />
          {n === 1 ? "1 recibo aplicado correctamente." : `${n} recibos aplicados correctamente.`}
          {enviados && (
            <span style={{ marginLeft: 8, color: "var(--text-secondary)", fontWeight: 400 }}>
              Correo enviado.
            </span>
          )}
          {error && (
            <span style={{ marginLeft: 8, color: "#f87171", fontWeight: 400 }}>{error}</span>
          )}
        </span>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            type="button"
            onClick={abrir}
            disabled={cargando}
            style={{
              padding: "6px 14px",
              background: "rgba(249,115,22,0.15)",
              border: "1px solid rgba(249,115,22,0.4)",
              borderRadius: 7,
              color: "#f97316",
              fontSize: "0.83rem",
              fontWeight: 600,
              cursor: cargando ? "wait" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <i className={`fa-solid ${cargando ? "fa-circle-notch fa-spin" : "fa-envelope"}`} />
            {n === 1 ? "Enviar correo al residente" : `Enviar correos (${n})`}
          </button>
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              title="Cerrar"
              style={{
                background: "none",
                border: "none",
                color: "#4ade80",
                cursor: "pointer",
                fontSize: "1rem",
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {abierto && recibos && (
        <SendReceiptEmailModal
          recibos={recibos}
          onClose={() => setAbierto(false)}
          onSuccess={() => setEnviados(true)}
        />
      )}
    </>
  );
}
